import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FolderGit2, PlayCircle, Link2, Table, ListTodo, FileCode2, SearchCheck } from 'lucide-react';
import { ActivepiecesEmbed } from '@/components/activepieces/ActivepiecesEmbed';
import { McpServerDashboard } from './McpServerDashboard';
import { TemplatesDashboard } from './TemplatesDashboard';
import { RunSearchDashboard } from './RunSearchDashboard';
import { SiteHeader } from '@/components/site-header';
import { Button } from '@/components/ui/button';

interface WorkflowDetailsProps {
  subRoute: string;
}

// Route configuration for headers
const ROUTE_CONFIG: Record<string, { 
  title: string; 
  subtitle: string; 
  icon: React.ElementType;
}> = {
  '/templates': {
    title: 'Templates',
    subtitle: 'Browse and use pre-built workflow templates',
    icon: FileCode2,
  },
  '/flows': {
    title: 'Flows',
    subtitle: 'Create and manage your automation flows',
    icon: FolderGit2,
  },
  '/runs': {
    title: 'Runs',
    subtitle: 'View execution history and monitor performance',
    icon: PlayCircle,
  },
  '/connections': {
    title: 'Connections',
    subtitle: 'Manage your integrated services and APIs',
    icon: Link2,
  },
  '/tables': {
    title: 'Tables',
    subtitle: 'View and manage your data tables',
    icon: Table,
  },
  '/todos': {
    title: 'Todos',
    subtitle: 'Manage your todo lists and tasks',
    icon: ListTodo,
  },
};

// Helper to check if a route is a list route (no ID)
const checkIsListRoute = (route: string): { isListRoute: boolean; routeConfig: typeof ROUTE_CONFIG[string] | null } => {
  // Extract just the base path without query params
  const pathWithoutQuery = route.split('?')[0];
  
  // List of exact list routes that should show header (for Activepieces embed routes only)
  // Templates route is handled separately by TemplatesDashboard with its own header
  const listRoutes = ['/flows', '/runs', '/connections', '/tables', '/todos'];
  
  // Normalize path (remove trailing slash)
  const normalizedPath = pathWithoutQuery.endsWith('/') 
    ? pathWithoutQuery.slice(0, -1) 
    : pathWithoutQuery;
  
  // Check if it's exactly one of the list routes
  const isExactListRoute = listRoutes.includes(normalizedPath);
  
  if (isExactListRoute && ROUTE_CONFIG[normalizedPath]) {
    return { 
      isListRoute: true, 
      routeConfig: ROUTE_CONFIG[normalizedPath] 
    };
  }
  
  return { isListRoute: false, routeConfig: null };
};

export const WorkflowDetails = React.memo(({ subRoute }: WorkflowDetailsProps) => {
  const navigate = useNavigate();
  
  // Track the current internal route from iframe navigation
  // This updates when user navigates within the iframe (e.g., clicking on a flow)
  const [currentInternalRoute, setCurrentInternalRoute] = useState<string>(subRoute);
  
  // Sync internal route state when subRoute prop changes (e.g., React Router navigation)
  // This handles cases like navigating from /flows to /runs via sidebar or browser
  useEffect(() => {
    setCurrentInternalRoute(subRoute);
  }, [subRoute]);
  
  // Check if this is an MCP route (either /mcp or /mcp/server-id)
  const isMcpRoute = subRoute === '/mcp' || subRoute.startsWith('/mcp/');
  
  // Check if this is a Templates route (either /templates or /templates/template-id)
  const isTemplatesRoute = subRoute === '/templates' || subRoute.startsWith('/templates/');
  
  // Check if this is a Run Search route
  const isRunSearchRoute = subRoute === '/runs-search' || subRoute.startsWith('/runs-search');
  
  // Check if this is an Activepieces embed route
  const isActivepiecesEmbedRoute = subRoute.match(/^\/(flows|runs|connections|tables|todos)/);
  
  // Determine if we should show header based on CURRENT internal route (not initial subRoute)
  // This ensures header hides when user navigates to detail view within iframe
  const { isListRoute, routeConfig } = useMemo(() => {
    return checkIsListRoute(currentInternalRoute);
  }, [currentInternalRoute]);

  const handleSdkConfigured = useCallback(() => {
    // SDK configured successfully
  }, []);

  // Update internal route state when iframe navigates
  const onInternalRouteChange = useCallback((newRoute: string) => {
    setCurrentInternalRoute(newRoute);
  }, []);

  const handleBackToWorkflows = useCallback(() => {
    navigate('/dashboard/workflows');
  }, [navigate]);

  // For Templates route, render the Templates dashboard with flex layout
  // Pass the template ID if present in the route
  if (isTemplatesRoute) {
    // Extract template ID from route like /templates/template-123
    const templateIdMatch = subRoute.match(/^\/templates\/(.+)$/);
    const templateId = templateIdMatch ? templateIdMatch[1] : null;
    
    return <TemplatesDashboard initialTemplateId={templateId} />;
  }

  // For MCP route, render the MCP dashboard (single server per project, no ID needed)
  if (isMcpRoute) {
    return <McpServerDashboard />;
  }

  // For Run Search route, render the Run Search dashboard
  if (isRunSearchRoute) {
    return <RunSearchDashboard />;
  }

  // For Activepieces embed routes
  if (isActivepiecesEmbedRoute) {
    return (
      <>
        {/* Only show header on list routes */}
        {isListRoute && routeConfig && (
          <SiteHeader 
            title={routeConfig.title}
            subtitle={routeConfig.subtitle}
            backButton={
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToWorkflows}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            }
          />
        )}
        <div className={isListRoute ? "flex-1 overflow-hidden" : "h-full w-full"}>
          <ActivepiecesEmbed 
            route={subRoute}
            onInternalRouteChange={onInternalRouteChange}
            onConfigured={handleSdkConfigured}
          />
        </div>
      </>
    );
  }

  // Fallback for any other routes
  return (
    <div className="container">
      <ActivepiecesEmbed 
        route={subRoute}
        onInternalRouteChange={onInternalRouteChange}
        onConfigured={handleSdkConfigured}
      />
    </div>
  );
});